import React from 'react';
import DdmPredicateEditor from './DdmPredicateEditor.jsx';

function isEmptyValue(value) {
  return value === undefined || value === null || value === '';
}

export function pruneDdmPayload(value) {
  if (Array.isArray(value)) {
    const next = value.map(pruneDdmPayload).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === 'object') {
    const next = {};
    for (const [key, child] of Object.entries(value)) {
      const pruned = pruneDdmPayload(child);
      if (pruned !== undefined) next[key] = pruned;
    }
    return Object.keys(next).length ? next : undefined;
  }
  if (isEmptyValue(value)) return undefined;
  return value;
}

function toDatetimeLocal(value) {
  if (!value || typeof value !== 'string') return '';
  return value.length >= 16 ? value.slice(0, 16) : value;
}

function fromDatetimeLocal(value) {
  if (!value) return undefined;
  return value.length === 16 ? `${value}:00` : value;
}

function supportedOsLabel(supportedOS) {
  if (!supportedOS || typeof supportedOS !== 'object') return '';
  return Object.entries(supportedOS)
    .filter(([, info]) => info && info.introduced && info.introduced !== 'n/a')
    .map(([os, info]) => `${os} ${info.introduced}+`)
    .join(', ');
}

function FieldHelp({ spec }) {
  const os = supportedOsLabel(spec.supportedOS);
  return (
    <>
      {spec.content ? <div className="form-text">{spec.content}</div> : null}
      {os ? <div className="form-text text-muted">{os}</div> : null}
    </>
  );
}

function ScalarField({ spec, value, onChange }) {
  const required = spec.presence === 'required';
  const id = `ddm-${spec.key}`;

  if (spec.type === 'boolean') {
    return (
      <select
        id={id}
        className="form-select"
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(e) => {
          if (e.target.value === '') onChange(undefined);
          else onChange(e.target.value === 'true');
        }}
      >
        {!required && <option value="">Nicht gesetzt</option>}
        <option value="true">Ja</option>
        <option value="false">Nein</option>
      </select>
    );
  }

  if (Array.isArray(spec.rangelist) && spec.rangelist.length) {
    return (
      <select
        id={id}
        className="form-select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      >
        {!required && <option value="">Nicht gesetzt</option>}
        {spec.rangelist.map((item) => (
          <option key={String(item)} value={item}>{String(item)}</option>
        ))}
      </select>
    );
  }

  if (spec.type === 'integer' || spec.type === 'real') {
    return (
      <input
        id={id}
        className="form-control"
        type="number"
        step={spec.type === 'real' ? 'any' : '1'}
        min={spec.range?.min}
        max={spec.range?.max}
        value={value ?? ''}
        required={required}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') onChange(undefined);
          else {
            const parsed = spec.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
            onChange(Number.isFinite(parsed) ? parsed : undefined);
          }
        }}
      />
    );
  }

  const isDateTime = spec.valuetype === 'localtime' || /datetime/i.test(spec.key || '') || /date time/i.test(spec.title || '');
  if (isDateTime) {
    return (
      <input
        id={id}
        className="form-control"
        type="datetime-local"
        value={toDatetimeLocal(value)}
        required={required}
        onChange={(e) => onChange(fromDatetimeLocal(e.target.value))}
      />
    );
  }

  const inputType = spec.valuetype === 'url' || /url$/i.test(spec.key || '') ? 'url' : 'text';
  return (
    <input
      id={id}
      className="form-control"
      type={inputType}
      value={value ?? ''}
      required={required}
      placeholder={spec.default != null ? String(spec.default) : ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
    />
  );
}

function DictionaryField({ spec, value, onChange, options }) {
  const required = spec.presence === 'required';
  const included = required || (value != null && typeof value === 'object');
  const subkeys = spec.subkeys || [];

  if (!required) {
    return (
      <div className="border rounded p-3 mb-2">
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id={`ddm-include-${spec.key}`}
            checked={included}
            onChange={(e) => onChange(e.target.checked ? (value && typeof value === 'object' ? value : {}) : undefined)}
          />
          <label className="form-check-label" htmlFor={`ddm-include-${spec.key}`}>
            {spec.title || spec.key} festlegen
          </label>
        </div>
        <FieldHelp spec={spec} />
        {included && (
          <div className="mt-2">
            {subkeys.map((child) => (
              <SchemaField
                key={child.key}
                spec={child}
                value={value?.[child.key]}
                onChange={(next) => onChange({ ...(value || {}), [child.key]: next })}
                options={options}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border rounded p-3 mb-2">
      <div className="fw-semibold mb-2">{spec.title || spec.key}</div>
      <FieldHelp spec={spec} />
      {subkeys.map((child) => (
        <SchemaField
          key={child.key}
          spec={child}
          value={value?.[child.key]}
          onChange={(next) => onChange({ ...(value || {}), [child.key]: next })}
          options={options}
        />
      ))}
    </div>
  );
}

function ArrayField({ spec, value, onChange, options }) {
  const itemSpec = spec.subkeys?.[0];
  const items = Array.isArray(value) ? value : [];
  if (!itemSpec) return null;

  const addItem = () => {
    if (itemSpec.type === 'dictionary') onChange([...items, {}]);
    else onChange([...items, '']);
  };

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label mb-0">
          {spec.title || spec.key}
          {spec.presence === 'required' ? ' *' : ''}
        </label>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addItem}>
          Eintrag hinzufügen
        </button>
      </div>
      <FieldHelp spec={spec} />
      {items.map((item, index) => (
        <div key={`${spec.key}-${index}`} className="d-flex gap-2 align-items-start mb-2">
          <div className="flex-grow-1">
            <SchemaField
              spec={{ ...itemSpec, presence: 'required', title: `${spec.title || spec.key} ${index + 1}` }}
              value={item}
              onChange={(next) => {
                const copy = [...items];
                copy[index] = next;
                onChange(copy);
              }}
              options={options}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger mt-4"
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          >
            Entfernen
          </button>
        </div>
      ))}
    </div>
  );
}

export function SchemaField({ spec, value, onChange, options }) {
  if (!spec?.key) return null;
  if (spec.key === 'Predicate') {
    return (
      <div className="mb-3">
        <label className="form-label">{spec.title || 'Aktivierungsbedingung'}</label>
        <FieldHelp spec={spec} />
        <DdmPredicateEditor value={value || ''} onChange={onChange} />
      </div>
    );
  }
  if (spec.key === 'StandardConfigurations' && Array.isArray(options?.configurations)) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="mb-3">
        <div className="form-label">{spec.title || 'Konfigurationen'}{spec.presence === 'required' ? ' *' : ''}</div>
        <FieldHelp spec={spec} />
        <div className="border rounded p-2" style={{ maxHeight: 240, overflow: 'auto' }}>
          {options.configurations.length === 0 ? (
            <p className="small text-muted mb-0">Noch keine Konfigurationen gespeichert.</p>
          ) : (
            options.configurations.map((item) => {
              const id = item.Identifier || item.identifier;
              return (
                <label key={id} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selected.includes(id)}
                    onChange={(e) => {
                      onChange(e.target.checked
                        ? [...selected, id]
                        : selected.filter((entry) => entry !== id));
                    }}
                  />
                  <span className="form-check-label">
                    <code>{id}</code>
                    {item.Type ? <span className="small text-muted"> · {item.Type.replace(/^com\.apple\.configuration\./, '')}</span> : null}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>
    );
  }
  if (spec.key === 'ProfileURL' && Array.isArray(options?.profiles)) {
    return (
      <div className="mb-3">
        <label className="form-label">{spec.title || 'Profil'}{spec.presence === 'required' ? ' *' : ''}</label>
        <select
          className="form-select"
          value={options.profiles.find((profile) => options.profileUrls?.[profile._id] === value)?._id || ''}
          onChange={async (e) => {
            const id = e.target.value;
            if (!id) {
              onChange(undefined);
              return;
            }
            if (options.profileUrls?.[id]) {
              onChange(options.profileUrls[id]);
              return;
            }
            if (typeof options.loadProfileUrl === 'function') {
              const url = await options.loadProfileUrl(id);
              onChange(url);
            }
          }}
        >
          <option value="">Profil wählen…</option>
          {options.profiles.map((profile) => (
            <option key={profile._id} value={profile._id}>
              {profile.PayloadDisplayName || profile.PayloadIdentifier}
            </option>
          ))}
        </select>
        <FieldHelp spec={spec} />
        {value ? <div className="form-text font-monospace">{value}</div> : null}
      </div>
    );
  }
  if (Array.isArray(spec.assettypes) && spec.assettypes.length && Array.isArray(options?.assets)) {
    const matching = options.assets.filter((item) => spec.assettypes.includes(item.Type || item.type));
    return (
      <div className="mb-3">
        <label className="form-label">{spec.title || spec.key}{spec.presence === 'required' ? ' *' : ''}</label>
        <select
          className="form-select"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          <option value="">Asset wählen…</option>
          {matching.map((item) => {
            const id = item.Identifier || item.identifier;
            return <option key={id} value={id}>{id}</option>;
          })}
        </select>
        <FieldHelp spec={spec} />
        {matching.length === 0 ? (
          <div className="form-text">Zuerst ein passendes Asset unter DDM anlegen ({spec.assettypes.join(', ')}).</div>
        ) : null}
      </div>
    );
  }
  if (spec.type === 'dictionary') {
    return <DictionaryField spec={spec} value={value} onChange={onChange} options={options} />;
  }
  if (spec.type === 'array') {
    return <ArrayField spec={spec} value={value} onChange={onChange} options={options} />;
  }
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={`ddm-${spec.key}`}>
        {spec.title || spec.key}
        {spec.presence === 'required' ? ' *' : ''}
      </label>
      <ScalarField spec={spec} value={value} onChange={onChange} />
      <FieldHelp spec={spec} />
    </div>
  );
}

export default function DdmSchemaForm({ schema, payload, onChange, options }) {
  if (!schema) return null;
  return (
    <div>
      {(schema.payloadkeys || []).map((spec) => (
        <SchemaField
          key={spec.key}
          spec={spec}
          value={payload?.[spec.key]}
          onChange={(next) => onChange({ ...(payload || {}), [spec.key]: next })}
          options={options}
        />
      ))}
    </div>
  );
}
